// 管理者: サーバー管理(設計書 §3.5.4)

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../client';

export type ServerStatus = 'online' | 'offline' | 'error' | 'maintenance';
export type SlotState = 'idle' | 'starting' | 'running' | 'stopping' | 'error';

export interface ServerSlot {
  slot_name: 'A' | 'B';
  state: SlotState;
  current_session: { performer_name: string; started_at: string } | null;
}

export interface AdminServer {
  ulid: string;
  name: string;
  status: ServerStatus;
  gpu_model: string | null;
  cpu_info: string | null;
  agent_version: string | null;
  uptime_seconds: number | null;
  last_heartbeat_at: string | null;
  slots: ServerSlot[];
}

export const adminServerKeys = {
  all: ['admin', 'servers'] as const,
};

export function useAdminServers() {
  return useQuery({
    queryKey: adminServerKeys.all,
    queryFn: () => apiRequest<AdminServer[]>('/admin/servers'),
    refetchInterval: 15_000,
  });
}

export interface CreateServerResponse {
  ulid: string;
  name: string;
  api_key: string;
}

export function useCreateServer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      apiRequest<CreateServerResponse>('/admin/servers', {
        method: 'POST',
        body: { name },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminServerKeys.all });
    },
  });
}

export function useRestartServer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ulid: string) =>
      apiRequest<void>(`/admin/servers/${ulid}/restart`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminServerKeys.all });
    },
  });
}

export function useDisableServer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ulid: string) =>
      apiRequest<void>(`/admin/servers/${ulid}/disable`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminServerKeys.all });
    },
  });
}

export const SERVER_STATUS_LABELS: Record<ServerStatus, string> = {
  online: 'オンライン',
  offline: 'オフライン',
  error: 'エラー',
  maintenance: 'メンテナンス',
};

export const SLOT_STATE_LABELS: Record<SlotState, string> = {
  idle: '待機',
  starting: '開始中',
  running: '配信中',
  stopping: '停止中',
  error: 'エラー',
};
